import { ClientMethodsTypes, client } from '@/services/v2/client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfile } from '../../profile/hooks/useProfile'
import { ProfileQueryKeys } from '../../profile/types/query-keys'
import { useEffect, useMemo, useState } from 'react'
import { useUser } from 'containers/user/hooks/useUser'
import { filterRecommendationsByUserAndProfile } from 'containers/recommendations/utilities/recommendations'
import { useDissmissedRecommendationsStore } from '../store/recommendations'
import { UserQueryKeys } from 'containers/user/types/user'
import {
  NetworkersQueryParams,
  ProfileRecommendation,
  ProfileRecommendationType,
} from '../types/recommendations'
import { cleanProfilesWithoutId } from 'containers/profile-network/misc/connections'

const BASE_URL = '/api/v1/users'

interface UseRecommendationsParams {
  customQueryKey?: ProfileQueryKeys
  type?: ProfileRecommendationType
}

export const useRecommendations = ({
  customQueryKey,
  type,
}: UseRecommendationsParams = {}) => {
  const { profile } = useProfile()
  const { user } = useUser()
  const [refresh, setRefresh] = useState(false)

  const id = profile?.id || user?.id

  const {
    data: recommendations,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      ProfileQueryKeys.RECOMMENDATIONS,
      customQueryKey,
      id,
      type,
      refresh,
    ],
    queryFn: () =>
      client<ProfileRecommendation[]>(
        `${BASE_URL}/recommendations/${id}${type ? `/${type}` : ''}${
          refresh ? '?refresh=true' : ''
        }`
      ),
    enabled: !!id,
  })

  useEffect(() => {
    if (
      !isLoading &&
      recommendations !== undefined &&
      recommendations?.length < 18
    ) {
      setRefresh(true)
    }
  }, [recommendations, isLoading])

  const filteredRecommendations = useMemo(() => {
    return filterRecommendationsByUserAndProfile({
      recommendations,
      user,
      profileId: profile?.id,
    })
  }, [profile?.id, recommendations, user])

  const [gpRecommendations, hotAndColdRecommendations] = useMemo(() => {
    const initialValue: [ProfileRecommendation[], ProfileRecommendation[]] = [
      [],
      [],
    ]
    return filteredRecommendations.reduce((acc, recommendation) => {
      const cloneAcc = [...acc]
      if (
        recommendation.recommendationType ===
        ProfileRecommendationType.GPP_PROFILE
      ) {
        cloneAcc[0].push(recommendation)
      } else {
        cloneAcc[1].push(recommendation)
      }
      return cloneAcc
    }, initialValue)
  }, [filteredRecommendations])

  return {
    recommendations: cleanProfilesWithoutId(
      filteredRecommendations || []
    ) as ProfileRecommendation[],
    gpRecommendations,
    hotAndColdRecommendations,
    isLoading,
    error,
  }
}

export const useNetworkersRecommendations = (
  params?: NetworkersQueryParams
) => {
  const { user } = useUser()
  const { profile } = useProfile()
  const [refresh, setRefresh] = useState(false)

  const id = profile?.id || user?.id

  const { data, ...rest } = useQuery({
    queryKey: [
      ProfileQueryKeys.RECOMMENDATIONS,
      ProfileQueryKeys.NETWORKERS,
      refresh,
      id,
    ],
    queryFn: () =>
      client<ProfileRecommendation[]>(
        `${BASE_URL}/recommendations/top/networkers/${id}${
          refresh ? '?refresh=true' : ''
        }`,
        {
          method: ClientMethodsTypes.POST,
          body: params ?? {},
        }
      ),
    enabled: !!id,
  })

  useEffect(() => {
    if (!rest.isLoading && data !== undefined && data?.length < 18) {
      setRefresh(true)
    }
  }, [data, rest?.isLoading])

  const networkers = useMemo(
    () =>
      filterRecommendationsByUserAndProfile({
        recommendations: data,
        user,
        profileId: profile?.id,
      }),
    [data, profile?.id, user]
  )

  return {
    networkers: cleanProfilesWithoutId(
      networkers || []
    ) as ProfileRecommendation[],
    ...rest,
  }
}

export const useInviteRecommendation = () => {
  const queryClient = useQueryClient()
  const { user } = useUser()
  return useMutation({
    mutationFn: (contact: ProfileRecommendation) =>
      client<ProfileRecommendation>(`${BASE_URL}/connections/invite-contact`, {
        method: ClientMethodsTypes.POST,
        body: contact,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([UserQueryKeys.USER, user.id])
    },
  })
}

export const useDismissRecommendation = () => {
  const { user } = useUser()
  const { dismissRecommendation } = useDissmissedRecommendationsStore()
  return useMutation({
    mutationFn: (recommendationId: string) =>
      client<ProfileRecommendation>(
        `${BASE_URL}/dismiss/recommendation?requestorId=${user.id}&dismissedId=${recommendationId}`,
        {
          method: ClientMethodsTypes.GET,
        }
      ),
    onSuccess: (_, recommendationId) => dismissRecommendation(recommendationId),
  })
}
